import * as XLSX from 'xlsx';

/**
 * Exact column order expected by RateQuality ShipmentDataToRate.cs
 * Order is CRITICAL - RateQuality uses positional mapping
 */
const RATE_QUALITY_COLUMN_ORDER = [
  // 1-5: Initial config columns
  'Systems To Rate (BG,MG,P44)',
  'BG Rating Account#',
  'MG P44 Account#',
  'Contract Names Filter',
  'Notify Email Address',

  // 6-32: Shipment data part 1
  'PRG',
  'Primary Reference',
  'Target Ship (Early)',
  'Origin City',
  'Origin State',
  'Origin Zip',
  'Origin Ctry',
  'Dest City',
  'Dest State',
  'Dest Zip',
  'Dest Ctry',
  'Item Weight',
  'Item Class',
  'Carrier Used',
  'Total',
  'Services',
  'Qty',
  'Qty UOM',
  'Item Length',
  'Item Width',
  'Item Height',
  'Dimensions UOM',
  'Shipment Linear Feet',
  'Service Days',
  'Origin Geo',
  'Dest Geo',
  'Mileage',

  // 33-37: Output columns
  'Error',
  'API Response(ms)',
  'FilePart',
  'Request',
  'Response',

  // 38-42: Additional config columns
  'Targeted Carriers',
  'Return Customer Rates',
  'Return Live Contract Rates',
  'Return Proposal Contract Rates',
  'Return Volume Rates',

  // 43-44: Shipment data part 2
  'Is Stackable',
  'Max Stacking Layers',
];

/**
 * Exact column order expected by RateQuality ParcelShipmentDataToRate.cs.
 * Forked from RATE_QUALITY_COLUMN_ORDER — no freight class, no stacking, no geo/mileage,
 * no LTL contract-rate config columns. Header-name matching is what actually matters
 * (Npoi.Mapper binds by header text, not raw position) — order is kept aligned with the
 * RateQuality entity anyway for easier diffing.
 */
export const PARCEL_RATE_COLUMN_ORDER = [
  // Config columns
  'BG Rating Account#',
  'Notify Email Address',
  'Return Customer Rates',
  'Rate UPS',
  'Rate FedEx',

  // Shipment data
  'PRG',
  'Primary Reference (Tracking #)',
  'Master Tracking (MPS only)',
  'Ship Date',
  'Origin City',
  'Origin State',
  'Origin Zip',
  'Origin Ctry',
  'Dest City',
  'Dest State',
  'Dest Zip',
  'Dest Ctry',
  'Entered Weight (lb)',
  'Length (in)',
  'Width (in)',
  'Height (in)',
  'Piece Count',
  'Package Type',
  'Current Carrier',
  'Current Service',
  'Residential (Y/N)',
  'Declared Value Amount',
  'Signature Option',
  'Saturday Delivery (Y/N)',
  'Dangerous Goods',
  'Non-Standard Packaging (Y/N)',

  // Output columns
  'Error',
  'API Response(ms)',
  'FilePart',
  'Request',
  'Response',
];

/**
 * Create workbook with single tab (merged config + shipment data)
 * Used for: Batch Rate, AP Testing, Rate Compare, GEO Mileage, Parcel Rate
 */
export const createWorkbook = async <
  TConfigSheetData extends Record<string, any>
>(
  configSheetData: TConfigSheetData,
  shipmentSheet?: XLSX.WorkSheet,
  useTwoTabFormat?: boolean,
  columnOrder: readonly string[] = RATE_QUALITY_COLUMN_ORDER
) => {
  const wb = XLSX.utils.book_new();

  // Two-tab format for Rating Analysis
  if (useTwoTabFormat) {
    const configSheet = XLSX.utils.json_to_sheet([configSheetData]);
    XLSX.utils.book_append_sheet(wb, configSheet, 'Configuration (Input)');

    if (shipmentSheet) {
      XLSX.utils.book_append_sheet(wb, shipmentSheet, 'Shipment (Input)');
    }

    return new Blob([XLSX.write(wb, { type: 'array' })], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
    });
  }

  // Single-tab format (default) - for Batch Rate, AP Testing, etc.
  if (shipmentSheet) {
    // Convert shipment sheet to JSON
    const shipmentData = XLSX.utils.sheet_to_json(shipmentSheet);

    // Build each row in the EXACT order RateQuality expects
    const mergedData = shipmentData.map((shipmentRow: any, index: number) => {
      const orderedRow: any = {};

      // Build row with columns in exact RateQuality order
      columnOrder.forEach((columnName) => {
        // Auto-generate PRG as sequential row number
        if (columnName === 'PRG') {
          orderedRow[columnName] = index + 1;
        }
        // Use config value if this is a config column, otherwise use shipment value
        else if (configSheetData.hasOwnProperty(columnName)) {
          orderedRow[columnName] = configSheetData[columnName];
        } else if (shipmentRow.hasOwnProperty(columnName)) {
          orderedRow[columnName] = shipmentRow[columnName];
        } else {
          // Column not provided - leave empty
          orderedRow[columnName] = '';
        }
      });

      return orderedRow;
    });

    const mergedSheet = XLSX.utils.json_to_sheet(mergedData, {
      header: columnOrder as string[],
    });
    XLSX.utils.book_append_sheet(wb, mergedSheet, 'Shipments');
  } else {
    // No shipment data, just config
    const configSheet = XLSX.utils.json_to_sheet([configSheetData]);
    XLSX.utils.book_append_sheet(wb, configSheet, 'Shipments');
  }

  return new Blob([XLSX.write(wb, { type: 'array' })], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;',
  });
};

export const getSheetFromUpload = async (
  uploadFieldValue: File[] | undefined
) => {
  if (!Array.isArray(uploadFieldValue) || !uploadFieldValue[0]) return;
  const [uploadedWorkbook] = uploadFieldValue;
  const fileData = await uploadedWorkbook.arrayBuffer();
  const workbook = XLSX.read(fileData);
  return workbook.Sheets[workbook.SheetNames[0]];
};

export type FullFormatConfig = {
  accountNumbers: string[];
  notifyEmails: string[];
  targetedCarrierScacs: string[];
  returnCustomerRates: boolean;
  returnLiveContractRates: boolean;
  returnProposalContractRates: boolean;
  returnVolumeRates: boolean;
};

export const parseFullFormatFirstRow = async (
  uploadFieldValue: File[] | undefined
): Promise<FullFormatConfig | null> => {
  if (!Array.isArray(uploadFieldValue) || !uploadFieldValue[0]) return null;
  const [file] = uploadFieldValue;
  const fileData = await file.arrayBuffer();
  const workbook = XLSX.read(fileData);
  const sheet = workbook.Sheets[workbook.SheetNames[0]];

  const allRows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1 });
  if (!allRows.length) return null;

  const headers = allRows[0] as string[];
  if (!headers.includes('BG Rating Account#')) return null;

  const dataRows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet);
  if (!dataRows.length) return null;
  const row = dataRows[0];

  const parseFlag = (val: any) => val === '1' || val === 1 || val === true;
  const splitComma = (val: any) =>
    String(val ?? '').split(',').map((s) => s.trim()).filter(Boolean);

  return {
    accountNumbers: splitComma(row['BG Rating Account#']),
    notifyEmails: splitComma(row['Notify Email Address']),
    targetedCarrierScacs: splitComma(row['Targeted Carriers']),
    returnCustomerRates: parseFlag(row['Return Customer Rates']),
    returnLiveContractRates: parseFlag(row['Return Live Contract Rates']),
    returnProposalContractRates: parseFlag(row['Return Proposal Contract Rates']),
    returnVolumeRates: parseFlag(row['Return Volume Rates']),
  };
};
